import { Box, TextField, Typography, useTheme } from "@mui/material";
import { useToast } from "@qubit/autoparts/src/toast/useToast";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { SettingsSectionWrapper } from "~/components/settings/SettingsSection";
import {
  countCleanableFlaggedBins,
  formatBinsToCleanWithPercent
} from "~/features/preview/bins/binSettings.utils";
import { LabelWithInfoTooltip } from "~/features/preview/components/LabelWithInfoTooltip";
import { OverviewFieldRow } from "~/features/settings/components/OverviewFieldRow";
import {
  settingsControlFontSize,
  settingsInputMinHeightPx,
  settingsOverviewFormControlMaxWidthPx
} from "~/features/settings/settings.styles";
import { useDebounce } from "~/hooks/useDebounce";
import { runAsyncSafely } from "~/lib/helpers";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import { selectWorkstationAutostoreGridId } from "~/redux/selectors/workstationsSelectors";
import {
  useEditCleaningScheduleMutation,
  useGetAutostoreGridQuery,
  useGetBinCleaningReportQuery,
  useGetFlaggedBinsQuery
} from "~/redux/warehouse/autostoreGrid.hooks";

import { LockedFieldControl } from "./LockedFieldControl";

const maintenanceFieldPlacement = {
  total: {
    gridColumn: { xs: "1", md: "1", lg: "1" },
    gridRow: { xs: "1", md: "1", lg: "1" }
  },
  daily: {
    gridColumn: { xs: "1", md: "2", lg: "2" },
    gridRow: { xs: "2", md: "1", lg: "1" }
  },
  daysPer: {
    gridColumn: { xs: "1", md: "2", lg: "3" },
    gridRow: { xs: "3", md: "2", lg: "1" }
  },
  flagged: {
    gridColumn: { xs: "1", md: "1", lg: "1" },
    gridRow: { xs: "4", md: "2", lg: "2" }
  },
  min: {
    gridColumn: { xs: "1", md: "1", lg: "2" },
    gridRow: { xs: "5", md: "3", lg: "2" }
  },
  max: {
    gridColumn: { xs: "1", md: "2", lg: "3" },
    gridRow: { xs: "6", md: "3", lg: "2" }
  }
} as const;

const MAINTENANCE_SAVE_DEBOUNCE_MS = 1000;

function isValidMaintenanceNumber(
  value: number | null | undefined
): value is number {
  return typeof value === "number" && !Number.isNaN(value);
}

export function MaintenanceSection() {
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();
  const theme = useTheme();
  const { palette } = theme;
  const isAdmin = useAppSelector(selectUserIsAdmin);

  const selectedAutostoreGridId = useAppSelector(
    selectWorkstationAutostoreGridId
  );

  const { data: selectedAutostoreGrid, isFetching } = useGetAutostoreGridQuery(
    selectedAutostoreGridId!,
    { skip: !selectedAutostoreGridId }
  );

  const { data: binCleaningReport } = useGetBinCleaningReportQuery(
    selectedAutostoreGridId!,
    { skip: !selectedAutostoreGridId }
  );

  const { data: flaggedBins } = useGetFlaggedBinsQuery(
    {
      autostoreGridId: selectedAutostoreGridId!,
      workstationId: undefined
    },
    { skip: !selectedAutostoreGridId }
  );

  const { dailyQuota, remainingBinsToClean, totalBins } =
    binCleaningReport ?? {};

  const serverCleaningCadence =
    selectedAutostoreGrid?.cleaningCadenceInDays ?? null;
  const serverMinCleaningTasks =
    selectedAutostoreGrid?.minCleaningTasks ?? null;
  const serverMaxCleaningTasks =
    selectedAutostoreGrid?.maxCleaningTasks ?? null;

  const flaggedBinsForMaintenanceCount = useMemo(
    () => countCleanableFlaggedBins(flaggedBins),
    [flaggedBins]
  );

  const [editCleaningSchedule] = useEditCleaningScheduleMutation();

  const { register, reset, watch } = useForm<{
    cleaningCadence: number | null;
    minCleaningTasks: number | null;
    maxCleaningTasks: number | null;
  }>();

  const cleaningCadence = watch("cleaningCadence");
  const minCleaningTasks = watch("minCleaningTasks");
  const maxCleaningTasks = watch("maxCleaningTasks");

  const debouncedCadence = useDebounce(
    cleaningCadence,
    MAINTENANCE_SAVE_DEBOUNCE_MS,
    cleaningCadence
  );
  const debouncedMinCleaningTasks = useDebounce(
    minCleaningTasks,
    MAINTENANCE_SAVE_DEBOUNCE_MS,
    minCleaningTasks
  );
  const debouncedMaxCleaningTasks = useDebounce(
    maxCleaningTasks,
    MAINTENANCE_SAVE_DEBOUNCE_MS,
    maxCleaningTasks
  );

  const hydratedGridIdRef = useRef<string | null>(null);
  const saveInFlightRef = useRef(false);
  const persistedServerValuesRef = useRef({
    cleaningCadence: serverCleaningCadence,
    minCleaningTasks: serverMinCleaningTasks,
    maxCleaningTasks: serverMaxCleaningTasks
  });

  useEffect(() => {
    persistedServerValuesRef.current = {
      cleaningCadence: serverCleaningCadence,
      minCleaningTasks: serverMinCleaningTasks,
      maxCleaningTasks: serverMaxCleaningTasks
    };
  }, [serverCleaningCadence, serverMaxCleaningTasks, serverMinCleaningTasks]);

  useEffect(() => {
    if (!selectedAutostoreGrid || isFetching) {
      return;
    }

    const gridId = selectedAutostoreGrid.autostoreGridId;
    const isInitialHydration = hydratedGridIdRef.current !== gridId;

    reset(
      {
        cleaningCadence: selectedAutostoreGrid.cleaningCadenceInDays ?? null,
        minCleaningTasks: selectedAutostoreGrid.minCleaningTasks ?? null,
        maxCleaningTasks: selectedAutostoreGrid.maxCleaningTasks ?? null
      },
      { keepDirtyValues: !isInitialHydration }
    );

    hydratedGridIdRef.current = gridId;
  }, [isFetching, reset, selectedAutostoreGrid]);

  useEffect(() => {
    void (async () => {
      if (isFetching || !selectedAutostoreGridId || saveInFlightRef.current) {
        return;
      }

      if (
        debouncedCadence !== cleaningCadence ||
        debouncedMinCleaningTasks !== minCleaningTasks ||
        debouncedMaxCleaningTasks !== maxCleaningTasks
      ) {
        return;
      }

      const {
        cleaningCadence: persistedCleaningCadence,
        minCleaningTasks: persistedMinCleaningTasks,
        maxCleaningTasks: persistedMaxCleaningTasks
      } = persistedServerValuesRef.current;

      const cadenceChanged =
        isValidMaintenanceNumber(debouncedCadence) &&
        debouncedCadence !== persistedCleaningCadence;
      const minChanged =
        isAdmin &&
        isValidMaintenanceNumber(debouncedMinCleaningTasks) &&
        debouncedMinCleaningTasks !== persistedMinCleaningTasks;
      const maxChanged =
        isAdmin &&
        isValidMaintenanceNumber(debouncedMaxCleaningTasks) &&
        debouncedMaxCleaningTasks !== persistedMaxCleaningTasks;

      if (!cadenceChanged && !minChanged && !maxChanged) {
        return;
      }

      saveInFlightRef.current = true;

      try {
        await runAsyncSafely(
          () =>
            editCleaningSchedule({
              autostoreGridId: selectedAutostoreGridId,
              cleaningCadenceInDays: cadenceChanged ? debouncedCadence : null,
              minCleaningTasks: minChanged ? debouncedMinCleaningTasks : null,
              maxCleaningTasks: maxChanged ? debouncedMaxCleaningTasks : null,
              maxBinFlaggedInventoryMoveTaskGroupSize: null
            }).unwrap(),
          {
            onSuccess: () => {
              if (cadenceChanged) {
                persistedServerValuesRef.current.cleaningCadence =
                  debouncedCadence;
              }
              if (minChanged) {
                persistedServerValuesRef.current.minCleaningTasks =
                  debouncedMinCleaningTasks;
              }
              if (maxChanged) {
                persistedServerValuesRef.current.maxCleaningTasks =
                  debouncedMaxCleaningTasks;
              }
              successToast(t("cleaning schedule update success"));
            },
            onError: (err) => {
              errorToast(getMessageFromRtkError(err));
            }
          }
        );
      } finally {
        saveInFlightRef.current = false;
      }
    })();
  }, [
    cleaningCadence,
    debouncedCadence,
    debouncedMaxCleaningTasks,
    debouncedMinCleaningTasks,
    editCleaningSchedule,
    errorToast,
    isAdmin,
    isFetching,
    maxCleaningTasks,
    minCleaningTasks,
    selectedAutostoreGridId,
    serverCleaningCadence,
    serverMaxCleaningTasks,
    serverMinCleaningTasks,
    successToast,
    t
  ]);

  const totalBinsToCleanDisplay = formatBinsToCleanWithPercent(
    remainingBinsToClean,
    totalBins
  );
  const dailyBinsToCleanDisplay =
    typeof dailyQuota === "number" ? dailyQuota.toLocaleString() : "";
  const flaggedBinsForMaintenanceDisplay =
    flaggedBinsForMaintenanceCount.toLocaleString("en-US");

  const editableFieldSx = {
    width: "100%",
    maxWidth: `min(${settingsOverviewFormControlMaxWidthPx}px, 10ch)`,
    "& .MuiOutlinedInput-root": {
      minHeight: settingsInputMinHeightPx,
      alignItems: "center"
    },
    "& .MuiInputBase-input": {
      boxSizing: "border-box",
      paddingTop: "8.5px",
      paddingBottom: "8.5px",
      fontSize: settingsControlFontSize,
      lineHeight: 1.35
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: palette.text.primary,
      color: palette.text.primary,
      opacity: 1
    }
  };

  return (
    <SettingsSectionWrapper title={t("maintenance")}>
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))"
          },
          columnGap: 4,
          rowGap: 2.5
        }}
      >
        <Box sx={{ minWidth: 0, ...maintenanceFieldPlacement.total }}>
          <OverviewFieldRow
            label={t("total bins to clean")}
            helperText={t(
              "# of bins left in the grid that haven't been cleaned within the cleaning cycle"
            )}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {totalBinsToCleanDisplay}
            </Typography>
          </OverviewFieldRow>
        </Box>

        <Box sx={{ minWidth: 0, ...maintenanceFieldPlacement.daily }}>
          <OverviewFieldRow
            label={
              <LabelWithInfoTooltip
                label={t("daily bins to clean")}
                tooltip={t("daily bins to clean info tooltip")}
              />
            }
            helperText={t("# of bins to be cleaned each day")}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {dailyBinsToCleanDisplay}
            </Typography>
          </OverviewFieldRow>
        </Box>

        <Box sx={{ minWidth: 0, ...maintenanceFieldPlacement.flagged }}>
          <OverviewFieldRow
            label={
              <LabelWithInfoTooltip
                label={t("bins flagged for maintenance")}
                tooltip={t("bins flagged for maintenance info tooltip")}
              />
            }
            helperText={t(
              "# of bins currently flagged for maintenance on this grid"
            )}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {flaggedBinsForMaintenanceDisplay}
            </Typography>
          </OverviewFieldRow>
        </Box>

        <Box sx={{ minWidth: 0, ...maintenanceFieldPlacement.daysPer }}>
          <OverviewFieldRow
            label={t("days per cleaning cycle")}
            helperText={t("# of days to clean all bins in grid")}
          >
            <TextField
              id="cleaning-period"
              placeholder={t("cleaning period")}
              size="small"
              type="number"
              variant="outlined"
              sx={editableFieldSx}
              inputProps={{
                "aria-label": t("days per cleaning cycle"),
                min: 0,
                max: 9999999
              }}
              {...register("cleaningCadence", { valueAsNumber: true })}
            />
          </OverviewFieldRow>
        </Box>

        <Box sx={{ minWidth: 0, ...maintenanceFieldPlacement.min }}>
          <OverviewFieldRow
            label={t("min cleaning tasks")}
            helperText={t("min number of bins that can be cleaned")}
          >
            {isAdmin ? (
              <TextField
                id="min-cleaning-tasks"
                placeholder={t("min cleaning tasks")}
                size="small"
                type="number"
                variant="outlined"
                sx={editableFieldSx}
                inputProps={{
                  "aria-label": t("min cleaning tasks"),
                  min: 0,
                  max: 9999999
                }}
                {...register("minCleaningTasks", { valueAsNumber: true })}
              />
            ) : (
              <LockedFieldControl
                id="min-cleaning-tasks"
                value={selectedAutostoreGrid?.minCleaningTasks ?? ""}
                aria-label={t("min cleaning tasks")}
              />
            )}
          </OverviewFieldRow>
        </Box>

        <Box sx={{ minWidth: 0, ...maintenanceFieldPlacement.max }}>
          <OverviewFieldRow
            label={t("max cleaning tasks")}
            helperText={t("max number of bins that can be cleaned")}
          >
            {isAdmin ? (
              <TextField
                id="max-cleaning-tasks"
                placeholder={t("max cleaning tasks")}
                size="small"
                type="number"
                variant="outlined"
                sx={editableFieldSx}
                inputProps={{
                  "aria-label": t("max cleaning tasks"),
                  min: 0,
                  max: 9999999
                }}
                {...register("maxCleaningTasks", { valueAsNumber: true })}
              />
            ) : (
              <LockedFieldControl
                id="max-cleaning-tasks"
                value={selectedAutostoreGrid?.maxCleaningTasks ?? ""}
                aria-label={t("max cleaning tasks")}
              />
            )}
          </OverviewFieldRow>
        </Box>
      </Box>
    </SettingsSectionWrapper>
  );
}
